/**
 * CustomerProfile — the ONLY shape a customer directory (customers/<id>/) has
 * to satisfy. FEATURE-20260905-react-playground-customer-profiles; the design
 * is docs/2026-09/20260904-customer-profiles.md.
 *
 * Every build has exactly one profile, chosen at build time (A8_CUSTOMER,
 * default a8) and reached through the `@customer` alias. Base code reads the
 * profile from src/customer/index.ts and never names a customer. Every key
 * here is a promise to every customer directory: change by addition only.
 */
import type React from "react";
import type { ThemeOptions } from "@mui/material";
import type { PageModule } from "../page-dsl/spec";
import type { NavItem } from "../components/navigation/nav-items";

/**
 * A hand-written React page on a route — the escape hatch for a page the
 * Page DSL cannot express (prose, a chart over an RPC). Prefer a DSL page:
 * a DSL page is model-checked and is what later moves into the page store.
 */
export type CustomerRoute = {
  /** "/app/pipeline-stats" */
  path: string;
  /** AuthWall roles; default ["ActivatedUser"] */
  roles?: string[];
  /** what React.lazy receives */
  component: () => Promise<{ default: React.ComponentType }>;
};

export type CustomerProfile = {
  /** matches the directory name, customers/<id>/ */
  id: string;
  /** shown as the document title unless brand.title says otherwise */
  name: string;
  brand?: {
    title?: string;
    /** merged over the base MUI theme */
    theme?: ThemeOptions;
  };
  /** where a user lands after login; default ROUTES.PAGE.FOR("jobs") */
  home?: string;
  nav: {
    /** the customer's own entries; they lead the menu, base entries follow */
    items: NavItem[];
    /** base categories to drop ("Demos", "Mesh") */
    hideCategories?: string[];
    /** individual base entries to drop, by path */
    hidePaths?: string[];
  };
  /** page name → loader; same name as a base page shadows the base page */
  pages: Record<string, () => Promise<PageModule>>;
  routes?: CustomerRoute[];
};

/**
 * `import.meta.glob("./pages/*.page.ts")` keyed by page NAME:
 * "./pages/schedules.page.ts" → "schedules".
 */
export function pagesByName(
  glob: Record<string, () => Promise<PageModule>>,
): Record<string, () => Promise<PageModule>> {
  const out: Record<string, () => Promise<PageModule>> = {};
  for (const [path, loader] of Object.entries(glob)) {
    const m = /([^/]+)\.page\.ts$/.exec(path);
    if (m) out[m[1]] = loader;
  }
  return out;
}

/**
 * A profile with its apps' pages and nav entries added: app pages under the profile's own (a profile
 * page of the same name shadows the app's), app nav entries after the profile's own.
 * FEATURE-20261030-app-pages-in-profile-build
 */
export function withApps(
  profile: CustomerProfile,
  appPages: Record<string, () => Promise<PageModule>>,
  appNav: NavItem[],
): CustomerProfile {
  if (Object.keys(appPages).length === 0 && appNav.length === 0) return profile;
  return {
    ...profile,
    pages: { ...appPages, ...profile.pages },
    nav: { ...profile.nav, items: [...profile.nav.items, ...appNav] },
  };
}
